import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../ui/dialog";
import {
  MapPin,
  CircleDollarSign,
  ChevronLeft,
  ChevronRight,
  Info,
  Layers
} from "lucide-react";

// The base URL from your previous code
const IMAGE_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const formatLocation = (location) => {
  if (!location) return "Location not provided";
  // If location is just an ID string like "1" (as seen in your JSON)
  if (typeof location === 'string' && location.length < 3) return `Storage Room ${location}`;

  try {
    const parsed = typeof location === 'string' ? JSON.parse(location) : location;
    return parsed.address || location;
  } catch {
    return location;
  }
};

const EquipmentRentDetailsModal = ({ open, onOpenChange, equipment }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when modal opens or equipment changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [equipment, open]);

  if (!open || !equipment) return null;

  // Mapping variables to your API Response keys
  const displayName = equipment.equipment_name || "Unnamed Equipment";
  const manufacturer = equipment.manufacturer || "N/A";
  const model = equipment.model_number || "N/A";
  const description = equipment.description || "No description available.";
  const dailyRate = equipment.daily_rental_rate || "0.00";
  const marketPrice = equipment.purchase_price || "N/A";

  // Array Mappings
  const photos = equipment.equipment_photos || [];
  const specs = equipment.equipment_specs || [];
  const accessories = equipment.equipment_accessories || [];
  const hasMultipleImages = photos.length > 1;

  const getImageUrl = () => {
    if (photos.length > 0) {
      const url = photos[currentImageIndex].file_url;
      // Check if URL is already absolute or needs the base
      return url.startsWith('http') ? url : `${IMAGE_BASE_URL}${url}`;
    }
    return "/images/misc/equipment_1.png"; // Local Fallback
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed p-0 flex flex-col overflow-hidden bg-white dark:bg-card border-none z-50
         /* MOBILE STYLES: Bottom Sheet */
         /* Added !top-auto and !translate-y-0 to prevent the button from being pushed down */
         bottom-0 left-0 right-0 w-full !h-[70dvh] rounded-t-[20px]
         !top-auto !translate-y-0 translate-x-0
         data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom

         /* DESKTOP STYLES: Right Drawer */
         md:top-0 md:right-0 md:left-auto md:!h-full md:w-[480px] md:rounded-none md:max-w-none
         md:!translate-x-0 md:!translate-y-0
         md:data-[state=open]:slide-in-from-right md:data-[state=open]:slide-in-from-bottom-0"
      >
        {/* ================= HEADER ================= */}
        <DialogHeader className="px-4 md:px-6 py-4 border-b dark:border-zinc-800 shrink-0">
          <DialogTitle className="text-lg font-bold dark:text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-indigo-500" />
            Equipment Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-4 md:pb-10">
          {/* Image Carousel Section */}
          <div className="relative md:mt-6 rounded-2xl overflow-hidden border dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900/50 group">
            <img
              src={getImageUrl()}
              alt={displayName}
              onError={(e) => { e.target.src = "/images/misc/equipment_1.png"; }}
              className="w-full h-[300px] object-contain p-4 transition-all duration-300"
            />

            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-2.5 py-1 rounded-full font-medium">
                  {currentImageIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>

          {/* Title Area */}
          <div className="mt-4 md:mt-6">
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              {displayName}
            </h2>
            <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500 dark:text-zinc-400">
              <MapPin className="w-4 h-4 text-rose-500" />
              {formatLocation(equipment.storage_location)}
            </div>
          </div>

          {/* Technical Details Grid */}
          <section className="mt-4 md:mt-8 border dark:border-zinc-800 rounded-2xl p-3 md:p-5 bg-gray-50/50 dark:bg-zinc-900/30">
            <h3 className="font-bold text-sm uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-5">
              Technical Details
            </h3>

            <div className="grid grid-cols-2 gap-x-4 gap-y-6">
              <div>
                <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-bold uppercase mb-1">Manufacturer</p>
                <p className="font-semibold text-gray-900 dark:text-zinc-200">{manufacturer}</p>
              </div>

              <div>
                <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-bold uppercase mb-1">Model</p>
                <p className="font-semibold text-gray-900 dark:text-zinc-200">{model}</p>
              </div>

              <div>
                <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-bold uppercase mb-1">Serial Number</p>
                <p className="font-mono text-xs font-semibold text-gray-700 dark:text-zinc-300">
                  {equipment.serial_number || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-bold uppercase mb-1">Market Price</p>
                <p className="font-semibold text-gray-900 dark:text-zinc-200">
                  {marketPrice !== "N/A" ? `$${marketPrice}` : "N/A"}
                </p>
              </div>
            </div>
          </section>

          {/* Specs & Accessories (Visible only if data exists) */}
          {(specs.length > 0 || accessories.length > 0) && (
             <section className="mt-4 md:mt-6 border dark:border-zinc-800 rounded-2xl p-3 md:p-5 bg-white dark:bg-zinc-900/10">
                <div className="flex items-center gap-2 mb-4">
                    <Layers className="w-4 h-4 text-emerald-500" />
                    <h3 className="font-bold text-sm uppercase tracking-wider dark:text-white">Specs & Extras</h3>
                </div>

                <div className="space-y-4">
                    {specs.map((spec, idx) => (
                        <div key={idx} className="flex justify-between border-b border-gray-100 dark:border-zinc-800 pb-2">
                            <span className="text-sm text-gray-500 dark:text-zinc-400">{spec.spec_name}</span>
                            <span className="text-sm font-medium dark:text-zinc-200">{spec.spec_value}</span>
                        </div>
                    ))}
                    <div className="flex flex-wrap gap-2 mt-2">
                        {accessories.map((acc, idx) => (
                            <span key={idx} className="text-[11px] px-2 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 rounded-md font-medium">
                                + {acc.accessory_name}
                            </span>
                        ))}
                    </div>
                </div>
             </section>
          )}

          {/* Description */}
          <section className="mt-4 md:mt-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">Description</h3>
            <p className="text-sm text-gray-600 dark:text-zinc-400 leading-relaxed">
              {description}
            </p>
          </section>
        </div>

        {/* ================= FOOTER (STICKY) ================= */}
        <div className="shrink-0 border-t dark:border-zinc-800 p-4 md:px-6 md:py-5 bg-white dark:bg-zinc-950 flex items-center justify-between shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
          <div>
            {/* <span className="text-[10px] uppercase text-gray-400 dark:text-zinc-500 font-bold tracking-widest">
              Daily Rental Rate
            </span> */}
            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 mt-0.5">
              <CircleDollarSign className="w-4 h-4 md:w-6 md:h-6" />
              <span className="text-sm md:text-2xl font-black">
                ${dailyRate}
              </span>
            </div>
          </div>

          <button
            onClick={() => onOpenChange(false)}
            className="px-6 py-2.5 bg-zinc-900 dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm hover:opacity-90 transition-all"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EquipmentRentDetailsModal;
